// src/components/modals/MotorOnlyModal.jsx
//
// Modal explicativo: por qué las estrategias del manual funcionan SOLO con
// la proyección del motor Edge Elite en vivo. Dos ejemplos reales (NO ENTRAR
// vs UNDER) con condiciones parecidas y resultados opuestos.
//
// Texto en español neutro ("tú"). Estilos inline. createPortal a body.
// Cierra con ESC, click en backdrop, botón X y botón "Entendido".

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

function useIsMobile(maxWidth = 640) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= maxWidth : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= maxWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [maxWidth]);
  return isMobile;
}

const Svg = ({ children, size = 20, color = 'currentColor', sw = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IconAlert = ({ size = 18, color = '#FFFFFF' }) => (
  <Svg size={size} color={color} sw={2.2}>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </Svg>
);
const IconClose = ({ color = 'var(--ink)' }) => (
  <Svg size={18} color={color}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>
);
const IconBulb = ({ color = 'var(--accent)' }) => (
  <Svg size={20} color={color}><path d="M9 18h6" /><path d="M10 22h4" />
    <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1V18h6v-1.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z" /></Svg>
);
const IconTarget = ({ color = 'var(--accent)' }) => (
  <Svg size={20} color={color}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></Svg>
);
const IconChart = ({ color = 'var(--accent)' }) => (
  <Svg size={20} color={color}><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></Svg>
);
const IconDirection = ({ color = 'var(--accent)' }) => (
  <Svg size={20} color={color}><path d="M12 3v18" /><path d="m7 8 5-5 5 5" /><path d="m17 16-5 5-5-5" /></Svg>
);
const IconShield = ({ color = 'var(--accent)' }) => (
  <Svg size={20} color={color}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></Svg>
);

function Row({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '3px 0', fontSize: 13 }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <span style={{ color: color || 'var(--ink)', fontWeight: 500, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function Ejemplo({ isMobile, badge, badgeColor, badgeBg, izq, der, derBorder, caja, cajaBorder }) {
  return (
    <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--line)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <span style={{
          display: 'inline-block', background: badgeBg, color: badgeColor,
          fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6,
        }}>{badge}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 6 }}>
            Lo que tú puedes ver solo
          </div>
          {izq.map((r, i) => <Row key={i} {...r} />)}
        </div>
        <div style={{
          flex: 1, background: 'var(--bg-elev)', border: `1px solid ${derBorder}`,
          borderRadius: 8, padding: '10px 12px',
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 6 }}>
            Lo que el motor calcula en vivo
          </div>
          {der.map((r, i) => <Row key={i} {...r} />)}
        </div>
      </div>
      <div style={{
        marginTop: 12, borderLeft: `3px solid ${cajaBorder}`, background: 'var(--bg-elev)',
        padding: '10px 14px', borderRadius: '0 8px 8px 0', fontSize: 13, color: 'var(--ink)', lineHeight: 1.5,
      }}>
        {caja}
      </div>
    </div>
  );
}

function MotorCard({ icon, title, text }) {
  return (
    <div style={{ background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        {icon}
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{title}</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.45 }}>{text}</div>
    </div>
  );
}

export default function MotorOnlyModal({ isOpen, onClose }) {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const padX = isMobile ? 18 : 28;

  return createPortal(
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog" aria-modal="true" aria-label="Por qué las estrategias funcionan solo con el motor"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: isMobile ? 10 : 20,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-elev)', borderRadius: 16, maxWidth: 660, width: '100%',
          maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        {/* HEADER */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: `24px ${padX}px 16px`,
          borderBottom: '1px solid var(--line)', position: 'sticky', top: 0, background: 'var(--bg-elev)', borderRadius: '16px 16px 0 0',
        }}>
          <div style={{
            width: 36, height: 36, minWidth: 36, borderRadius: '50%', background: 'var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <IconAlert size={18} />
          </div>
          <h2 style={{ flex: 1, margin: 0, fontSize: 18, fontWeight: 500, color: 'var(--ink)', lineHeight: 1.3 }}>
            ¿Por qué las estrategias funcionan solo con el motor?
          </h2>
          <button type="button" onClick={onClose} aria-label="Cerrar"
            style={{ width: 28, height: 28, border: 'none', background: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconClose />
          </button>
        </div>

        {/* INTRO */}
        <div style={{ padding: `20px ${padX}px`, fontSize: 14, color: 'var(--ink)', lineHeight: 1.55 }}>
          Las reglas que te entregamos en tutoriales son la forma de leer las señales que emite
          el motor. Para entender por qué no funcionan solas, mira estos dos partidos reales con
          condiciones muy parecidas pero resultados opuestos.
        </div>

        {/* EJEMPLOS */}
        <div style={{ padding: `0 ${padX}px` }}>
          <Ejemplo
            isMobile={isMobile}
            badge="No entrar" badgeColor="#FFFFFF" badgeBg="var(--neg)"
            derBorder="var(--neg-soft)" cajaBorder="var(--neg)"
            izq={[
              { label: 'Score', value: '78 – 74 (Q3 min 5)' },
              { label: 'Línea casa de apuestas', value: '232' },
              { label: 'Liga', value: 'NBA (recomendada)' },
              { label: 'Ventana', value: 'no válida (min 5)', color: 'var(--neg)' },
            ]}
            der={[
              { label: 'Proyección', value: '223.5 pts', color: 'var(--neg)' },
              { label: 'Edge', value: '−8.5', color: 'var(--neg)' },
              { label: 'Veredicto', value: 'SIN VENTAJA', color: 'var(--neg)' },
              { label: 'Stake', value: '0u (no jugar)', color: 'var(--neg)' },
            ]}
            caja={
              <>Sin el motor: podrías pensar que la liga y la línea son válidas y apostar igual,
              pero la ventana no se cumple (estás en Q3 min 5 en lugar de min 1 o ≥3) y el edge
              real es solo −8.5. Apostarías UNDER 232 sin saberlo → pierdes dinero.</>
            }
          />
          <Ejemplo
            isMobile={isMobile}
            badge="UNDER 1.5u" badgeColor="#FFFFFF" badgeBg="var(--pos)"
            derBorder="var(--pos-soft)" cajaBorder="var(--pos)"
            izq={[
              { label: 'Score', value: '56 – 52 (Q3 min 4)' },
              { label: 'Línea casa de apuestas', value: '228' },
              { label: 'Liga', value: 'NBA (recomendada)' },
              { label: 'Ventana', value: 'válida (Q3 min 4 ≥ 3)', color: 'var(--pos)' },
            ]}
            der={[
              { label: 'Proyección', value: '207 pts', color: 'var(--pos)' },
              { label: 'Edge', value: '−21', color: 'var(--pos)' },
              { label: 'Veredicto', value: 'UNDER', color: 'var(--pos)' },
              { label: 'Stake', value: '1.5u', color: 'var(--pos)' },
            ]}
            caja={
              <>Con el motor: la ventana es válida y el motor confirma edge −21 (supera el mínimo
              de −15). Se emite señal UNDER 228 con stake 1.5u. Apuestas con ventaja real.</>
            }
          />
        </div>

        {/* PIVOT */}
        <div style={{ padding: `0 ${padX}px`, marginBottom: 20 }}>
          <div style={{
            background: 'var(--accent-soft)', border: '1px solid #FFB388', borderRadius: 12,
            padding: 16, display: 'flex', gap: 12,
          }}>
            <div style={{ minWidth: 20 }}><IconBulb /></div>
            <div style={{ fontSize: 13.5, color: 'var(--ink)', lineHeight: 1.55 }}>
              <b>La diferencia clave:</b> a primera vista, ambos partidos parecen tener condiciones
              similares (NBA, línea normal, partido en Q3). Pero solo uno cumple TODAS las condiciones
              a la vez: ventana válida + edge suficiente + veredicto del motor. Sin la proyección del
              motor calculada en vivo, no hay forma de distinguirlos.
            </div>
          </div>
        </div>

        {/* GRID 2x2 */}
        <div style={{ padding: `0 ${padX}px`, marginBottom: 20 }}>
          <h3 style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', margin: '0 0 12px' }}>
            Lo que solo el motor puede saber en vivo
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
            <MotorCard icon={<IconTarget />} title="Proyección real"
              text="Cuántos puntos termina haciendo el partido según pace en vivo + datos históricos." />
            <MotorCard icon={<IconChart />} title="Edge calculado"
              text="La diferencia entre proyección y línea. Sin proyección no hay edge." />
            <MotorCard icon={<IconDirection />} title="Veredicto UNDER/OVER"
              text="Dirección de apuesta validada por el modelo cuantitativo." />
            <MotorCard icon={<IconShield />} title="Validación de liga"
              text="Si esa liga tiene patrón estadístico positivo en esa ventana." />
          </div>
        </div>

        {/* CONCLUSIÓN */}
        <div style={{ padding: `0 ${padX}px`, marginBottom: 20 }}>
          <div style={{ background: '#0A0A0C', borderRadius: 12, padding: 18 }}>
            <div style={{ fontSize: 15, color: '#FFFFFF', lineHeight: 1.5 }}>
              <b style={{ color: '#FF8A4C' }}>El motor te entrega la señal accionable.</b>
            </div>
            <div style={{ fontSize: 13.5, color: '#B4B2A9', lineHeight: 1.55, marginTop: 6 }}>
              Tú sigues la señal cuando aparece en el dashboard o en el Telegram VIP. Las reglas del
              manual son la forma de interpretar esa señal, no una receta para apostar por tu cuenta.
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div style={{
          padding: `16px ${padX}px`, borderTop: '1px solid var(--line)',
          display: 'flex', justifyContent: 'flex-end',
          position: 'sticky', bottom: 0, background: 'var(--bg-elev)', borderRadius: '0 0 16px 16px',
        }}>
          <button type="button" onClick={onClose}
            style={{
              background: 'var(--accent)', color: '#FFFFFF', border: 'none',
              padding: '9px 20px', borderRadius: 8, fontSize: 14, fontWeight: 500,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
            Entendido
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
